import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ListItem } from '@ui/components/List'
import { PopoverCard, PopoverList } from '@ui/components/Popover'
import { getRichFlag } from '@ui/icons/flags'

import { CountryPreferenceStrippedDto } from '@model/CountryDto'

import { InputSearch } from '@shared/components/InputSearch'

export interface CountrySelectorProps {
  countries: CountryPreferenceStrippedDto[]
  selectedCountry: CountryPreferenceStrippedDto | null
  onCountrySelect: (country: CountryPreferenceStrippedDto) => void
  isOpen: boolean
  onClose: () => void
}

export const CountrySelector: React.FC<CountrySelectorProps> = ({
  countries,
  selectedCountry,
  onCountrySelect,
  isOpen,
  onClose,
}) => {
  const [searchValue, setSearchValue] = useState('')
  const [filteredCountries, setFilteredCountries] = useState(countries)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation()

  useEffect(() => {
    setFilteredCountries(countries)
  }, [countries])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose()
        setSearchValue('')
        setFilteredCountries(countries)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, countries, onClose])

  const handleSearch = (value: string) => {
    setSearchValue(value)
    if (!value) {
      setFilteredCountries(countries)
    } else {
      setFilteredCountries(
        countries.filter(
          (country) =>
            country.country.name.toLowerCase().includes(value.toLowerCase()) ||
            (country.phoneCode || '').includes(value)
        )
      )
    }
  }

  const handleCountrySelect = (country: CountryPreferenceStrippedDto) => {
    onCountrySelect(country)
    onClose()
    setSearchValue('')
    setFilteredCountries(countries)
  }

  if (!isOpen) return null

  return (
    <div ref={dropdownRef}>
      <PopoverCard>
        <InputSearch
          label={t('Search')}
          value={searchValue}
          onChange={(e) => handleSearch(e.target.value)}
          onClear={() => handleSearch('')}
          isAutoFocus
        />
        <PopoverList variant='compact'>
          {filteredCountries.map((country) => (
            <ListItem
              key={country.country.id}
              onClick={() => handleCountrySelect(country)}
              active={country.country.id === selectedCountry?.country.id}
              size='lg'
              startSlot={{
                shape: 'circle',
                bgImage: getRichFlag(country.country.id),
              }}
            >{`${country.country.name} (${country.phoneCode})`}</ListItem>
          ))}
        </PopoverList>
      </PopoverCard>
    </div>
  )
}
